/**
 * Camada de edição sobre a sessão viva (design.md, Decisão 11).
 * Não é rota: quem abre, cancela e grava é a view.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as TeclaReact } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ReviewCard } from '../../../sdk/@types/Revisao';
import OcclusionEditor from '../OcclusionEditor/OcclusionEditor';
import { MASK_REQUIRED_MESSAGE, STUDY_MODE_OPTIONS } from '../OcclusionEditor/occlusionStudy';
import CompositorPreview from './CompositorPreview';
import type { FacePrevia } from './cartaoDaPrevia';
import { EditorHistorico } from './EditorHistorico';
import {
  ABAS_EDITOR,
  cartaoComConteudoAceito,
  comandoDaCamada,
  corpoDoSalvar,
  lerHistorico,
  rascunhoDoCartao,
  rotuloPausaFila,
  type AbaEditor,
  type EditorNaRevisaoProps,
  type HistoricoEditor,
  type RascunhoEditor,
} from './editorNaRevisaoContrato';
import FormatToolbar from './FormatToolbar';
import { formaInicial, rotulosDaForma } from './formaAutoria';
import { inserirFormato, type FormatoInserivel } from './formatacaoCartao';
import AlvoDeImagemDaFace, {
  type AlvoDeImagemDaFaceHandle,
} from './imagem/AlvoDeImagemDaFace';
import { revisaoPrimaryActionClass } from './revisaoSurfaces';

const FALHA_SALVAR = 'Não foi possível salvar.';

const campoClass =
  'w-full rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink';

const kbdClass =
  'shrink-0 rounded-chip border border-edge bg-surface-muted px-2 py-0.5 font-mono text-xs text-ink-secondary';

function lerSelecao(value: string, el: HTMLTextAreaElement | null): [number, number] {
  return [el?.selectionStart ?? value.length, el?.selectionEnd ?? value.length];
}

function enunciadoVinculado(card: ReviewCard): string | null {
  const question = card.question;
  if (!question || typeof question !== 'object') return null;
  const campos = question as { statement?: unknown; enunciado?: unknown };
  if (typeof campos.statement === 'string' && campos.statement.trim() !== '') {
    return campos.statement;
  }
  if (typeof campos.enunciado === 'string' && campos.enunciado.trim() !== '') {
    return campos.enunciado;
  }
  return null;
}

/** Teclas que não são Esc / ⌘↵ ficam no controle focado e não sobem até a sessão. */
function conterNaCamada(event: TeclaReact<HTMLDivElement>): void {
  if (comandoDaCamada(event)) return;
  event.stopPropagation();
}

export function EditorNaRevisao(props: EditorNaRevisaoProps): JSX.Element {
  const {
    card,
    posicao,
    total,
    estreito,
    materias,
    onCancelar,
    onSalvo,
    salvar,
    carregarHistorico,
  } = props;

  const [idVisto, setIdVisto] = useState(card.id);
  const [draft, setDraft] = useState<RascunhoEditor>(() => rascunhoDoCartao(card));
  const [aba, setAba] = useState<AbaEditor>(props.abaInicial ?? 'conteudo');
  const [historico, setHistorico] = useState<HistoricoEditor>({ estado: 'carregando' });
  const [tagNova, setTagNova] = useState('');
  const [facePrevia, setFacePrevia] = useState<FacePrevia>('frente');
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [pediuMascara, setPediuMascara] = useState(false);

  const pedidoRef = useRef(0);
  const salvandoRef = useRef(false);
  const frontRef = useRef<HTMLTextAreaElement>(null);
  const backRef = useRef<HTMLTextAreaElement>(null);
  const frontAlvoRef = useRef<AlvoDeImagemDaFaceHandle>(null);
  const backAlvoRef = useRef<AlvoDeImagemDaFaceHandle>(null);
  const raizRef = useRef<HTMLDivElement>(null);
  const selecaoPendente = useRef<{ campo: 'front' | 'back'; start: number; end: number } | null>(
    null,
  );
  const carregarRef = useRef(carregarHistorico);
  carregarRef.current = carregarHistorico;
  const acoes = useRef<{ cancelar: () => void; salvar: () => void }>({
    cancelar: () => {},
    salvar: () => {},
  });

  if (card.id !== idVisto) {
    pedidoRef.current += 1;
    salvandoRef.current = false;
    selecaoPendente.current = null;
    setIdVisto(card.id);
    setDraft(rascunhoDoCartao(card));
    setTagNova('');
    setFalha(null);
    setPediuMascara(false);
    setFacePrevia('frente');
    setHistorico({ estado: 'carregando' });
    setSalvando(false);
  }

  const baseId = useId();
  const tituloId = `${baseId}-titulo`;
  const painelId = `${baseId}-painel`;
  const frenteId = `${baseId}-frente`;
  const versoId = `${baseId}-verso`;
  const tagId = `${baseId}-tag`;
  const forma = formaInicial(card.layout);
  const rotulos = rotulosDaForma(forma);
  const subjectName = materias.find((materia) => materia.id === draft.subjectId)?.name;
  const materiaListada = materias.some((materia) => materia.id === draft.subjectId);
  // A questão vinculada não grava marcações: o PUT leva só matéria e tags.
  const exigeMarcacao = card.source !== 'question' && card.layout === 'occlusion';
  const semMarcacao = exigeMarcacao && draft.masks.length === 0;

  const inserirNoCampo = (campo: 'front' | 'back', formato: FormatoInserivel) => {
    const el = campo === 'front' ? frontRef.current : backRef.current;
    const value = campo === 'front' ? draft.front : draft.back;
    const [start, end] = lerSelecao(value, el);
    const next = inserirFormato(value, start, end, formato);
    selecaoPendente.current = { campo, start: next.selectionStart, end: next.selectionEnd };
    setDraft((atual) => ({ ...atual, [campo]: next.value }));
  };

  const inserirImagemNoCampo = (campo: 'front' | 'back', fileKey: string) => {
    const el = campo === 'front' ? frontRef.current : backRef.current;
    const value = el?.value ?? (campo === 'front' ? draft.front : draft.back);
    const [start, end] = lerSelecao(value, el);
    const token = `![imagem](/files/${fileKey})`;
    const next = value.slice(0, start) + token + value.slice(end);
    const selectionStart = start + token.length;
    selecaoPendente.current = { campo, start: selectionStart, end: selectionStart };
    setDraft((atual) => ({ ...atual, [campo]: next }));
  };

  // O React reescreve o valor do textarea no commit e joga o cursor para o fim.
  // A seleção volta aqui, depois desse commit, para o trecho que a barra inseriu.
  useLayoutEffect(() => {
    const pendente = selecaoPendente.current;
    if (!pendente) return;
    selecaoPendente.current = null;
    const el = pendente.campo === 'front' ? frontRef.current : backRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pendente.start, pendente.end);
  });

  useLayoutEffect(() => {
    const raiz = raizRef.current;
    if (!raiz) return;
    const ativo = document.activeElement;
    if (ativo instanceof Node && raiz.contains(ativo) && ativo !== raiz) return;
    raiz.focus({ preventScroll: true });
  }, [estreito, card.id]);

  useEffect(() => {
    if (aba !== 'historico') return undefined;
    let ativo = true;
    setHistorico({ estado: 'carregando' });
    lerHistorico(card.id, (id) => carregarRef.current(id)).then((resultado) => {
      if (ativo) setHistorico(resultado);
    });
    return () => {
      ativo = false;
    };
  }, [aba, card.id]);

  useEffect(() => {
    const aoTeclar = (event: KeyboardEvent) => {
      const comando = comandoDaCamada(event);
      if (comando === 'cancelar') {
        event.preventDefault();
        event.stopPropagation();
        acoes.current.cancelar();
        return;
      }
      if (comando === 'salvar') {
        event.preventDefault();
        event.stopPropagation();
        acoes.current.salvar();
      }
    };
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, []);

  const cancelar = () => {
    pedidoRef.current += 1;
    salvandoRef.current = false;
    setSalvando(false);
    onCancelar();
  };

  const executarSalvar = () => {
    if (salvandoRef.current) return;
    if (semMarcacao) {
      setPediuMascara(true);
      setFalha(null);
      return;
    }
    setPediuMascara(false);
    setFalha(null);
    const pedido = pedidoRef.current;
    const corpo = corpoDoSalvar(card, draft);
    const aceito = cartaoComConteudoAceito(card, draft);
    salvandoRef.current = true;
    setSalvando(true);
    void Promise.resolve()
      .then(() => salvar(card.id, corpo))
      .then(
        (resultado) => {
          if (pedido !== pedidoRef.current) return;
          salvandoRef.current = false;
          setSalvando(false);
          // `{ updated: false }` não é o cartão aceito. A fila fica como estava.
          if (resultado != null && resultado.updated === false) {
            setFalha(FALHA_SALVAR);
            return;
          }
          onSalvo(aceito);
        },
        () => {
          if (pedido !== pedidoRef.current) return;
          salvandoRef.current = false;
          setSalvando(false);
          setFalha(FALHA_SALVAR);
        },
      );
  };

  acoes.current = { cancelar, salvar: executarSalvar };

  const escolherAba = (proxima: AbaEditor) => {
    if (proxima === aba) return;
    setAba(proxima);
    if (proxima === 'historico') setHistorico({ estado: 'carregando' });
  };

  const adicionarTag = () => {
    const valor = tagNova.trim();
    setTagNova('');
    if (!valor) return;
    setDraft((atual) =>
      atual.tags.includes(valor) ? atual : { ...atual, tags: [...atual.tags, valor] },
    );
  };

  const enunciado = enunciadoVinculado(card);

  const quadro = (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 flex-col gap-1 border-b border-edge px-4 py-3">
        <h2 id={tituloId} className="text-base font-semibold text-ink">
          Voltar à revisão
        </h2>
        <p data-editor-pausa="" data-pausa-fila="" className="text-sm tabular-nums text-ink-secondary">
          {rotuloPausaFila(posicao, total)}
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Seções do editor"
        className="grid shrink-0 grid-cols-3 gap-2 border-b border-edge px-4 py-3"
      >
        {ABAS_EDITOR.map((abaItem) => {
          const selecionada = aba === abaItem.id;
          return (
            <button
              key={abaItem.id}
              type="button"
              role="tab"
              id={`${baseId}-${abaItem.id}`}
              data-editor-aba={abaItem.id}
              aria-selected={selecionada}
              aria-controls={painelId}
              onClick={() => escolherAba(abaItem.id)}
              className={`min-w-0 rounded-chip border px-2 py-2 text-center text-sm ${
                selecionada
                  ? 'border-select/[0.42] bg-select/[0.14] font-semibold text-select-ink'
                  : 'border-edge font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink'
              }`}
            >
              {abaItem.rotulo}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={painelId}
        aria-labelledby={`${baseId}-${aba}`}
        tabIndex={0}
        data-editor-historico={aba === 'historico' ? historico.estado : undefined}
        aria-live={aba === 'historico' ? 'polite' : undefined}
        aria-busy={aba === 'historico' && historico.estado === 'carregando'}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
      >
        {aba === 'conteudo' && card.source === 'question' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink">O enunciado é a questão vinculada.</p>
            {enunciado ? (
              <p className="whitespace-pre-wrap rounded-input border border-edge bg-surface-muted px-3 py-2 text-sm text-ink">
                {enunciado}
              </p>
            ) : null}
          </div>
        ) : null}

        {aba === 'conteudo' && card.source !== 'question' && card.layout === 'occlusion' ? (
          <div className="min-w-0 [&>div]:!flex-col">
            <OcclusionEditor
              frontImageKey={draft.frontImageKey || undefined}
              masks={draft.masks}
              studyMode={draft.studyMode}
              onImageChange={(fileKey) =>
                setDraft((atual) => ({ ...atual, frontImageKey: fileKey ?? '' }))
              }
              onMasksChange={(masks) => setDraft((atual) => ({ ...atual, masks }))}
              onStudyModeChange={(studyMode) => setDraft((atual) => ({ ...atual, studyMode }))}
              explanation={draft.back}
              onExplanationChange={(back) => setDraft((atual) => ({ ...atual, back }))}
            />
          </div>
        ) : null}

        {aba === 'conteudo' && card.source !== 'question' && card.layout !== 'occlusion' && rotulos ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor={frenteId} className="text-sm font-medium text-ink">
                {rotulos.front}
              </label>
              <AlvoDeImagemDaFace
                ref={frontAlvoRef}
                onImagemEnviada={(chave) => inserirImagemNoCampo('front', chave)}
              />
              <FormatToolbar
                campo="Frente"
                onInserir={(formato) => inserirNoCampo('front', formato)}
                onAbrirAlvo={() => frontAlvoRef.current?.abrir()}
              />
              <textarea
                id={frenteId}
                ref={frontRef}
                rows={4}
                value={draft.front}
                onChange={(event) => {
                  const value = event.target.value;
                  setDraft((atual) => ({ ...atual, front: value }));
                }}
                onFocus={() => setFacePrevia('frente')}
                className={`${campoClass} resize-y`}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor={versoId} className="text-sm font-medium text-ink">
                {rotulos.back}
              </label>
              <AlvoDeImagemDaFace
                ref={backAlvoRef}
                onImagemEnviada={(chave) => inserirImagemNoCampo('back', chave)}
              />
              <FormatToolbar
                campo="Verso"
                onInserir={(formato) => inserirNoCampo('back', formato)}
                onAbrirAlvo={() => backAlvoRef.current?.abrir()}
              />
              <textarea
                id={versoId}
                ref={backRef}
                rows={4}
                value={draft.back}
                onChange={(event) => {
                  const value = event.target.value;
                  setDraft((atual) => ({ ...atual, back: value }));
                }}
                onFocus={() => setFacePrevia('verso')}
                className={`${campoClass} resize-y`}
              />
              {rotulos.backHint ? (
                <span className="text-xs text-ink-muted">{rotulos.backHint}</span>
              ) : null}
            </div>
            <CompositorPreview
              forma={forma}
              front={draft.front}
              back={draft.back}
              frontImageKey={draft.frontImageKey || undefined}
              masks={draft.masks}
              studyMode={draft.studyMode}
              subjectId={draft.subjectId}
              tags={draft.tags}
              subjectName={subjectName}
              face={facePrevia}
              onFaceChange={setFacePrevia}
            />
          </div>
        ) : null}

        {aba === 'ajustes' ? (
          <div className="flex flex-col gap-4">
            <label className="flex w-full flex-col gap-2">
              <span className="text-sm font-medium text-ink">Baralho</span>
              <select
                data-editor-baralho=""
                value={draft.subjectId}
                onChange={(event) => {
                  const subjectId = event.target.value;
                  setDraft((atual) => ({ ...atual, subjectId }));
                }}
                className={campoClass}
              >
                {draft.subjectId === '' ? <option value="">Selecione o baralho</option> : null}
                {draft.subjectId !== '' && !materiaListada ? (
                  <option value={draft.subjectId}>{draft.subjectId}</option>
                ) : null}
                {materias.map((materia) => (
                  <option key={materia.id} value={materia.id}>
                    {materia.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-col gap-2">
              <label htmlFor={tagId} className="text-sm font-medium text-ink">
                Tags
              </label>
              {draft.tags.length > 0 ? (
                <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                  {draft.tags.map((tag, indice) => (
                    <li key={`${tag}-${indice}`}>
                      <span className="inline-flex items-center gap-1 rounded-chip border border-edge bg-surface-muted py-1 pl-2.5 pr-1 text-sm text-ink">
                        {tag}
                        <button
                          type="button"
                          aria-label={`Remover ${tag}`}
                          onClick={() =>
                            setDraft((atual) => ({
                              ...atual,
                              tags: atual.tags.filter((_, i) => i !== indice),
                            }))
                          }
                          className="inline-flex h-6 w-6 items-center justify-center rounded-chip text-ink-muted hover:bg-surface-hover hover:text-ink"
                        >
                          <X size={14} aria-hidden />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <input
                id={tagId}
                type="text"
                value={tagNova}
                autoComplete="off"
                placeholder="Adicionar tag e pressionar Enter"
                onChange={(event) => setTagNova(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return;
                  if (event.nativeEvent.isComposing) return;
                  event.preventDefault();
                  adicionarTag();
                }}
                className={campoClass}
              />
            </div>
            {card.layout === 'occlusion' ? (
              <div className="flex w-full flex-col gap-2">
                <p id={`${baseId}-modo`} className="text-sm font-medium text-ink">
                  Modo de oclusão
                </p>
                <div
                  role="radiogroup"
                  aria-labelledby={`${baseId}-modo`}
                  data-editor-modo={draft.studyMode}
                  className="flex w-full flex-col gap-2"
                >
                {STUDY_MODE_OPTIONS.map((option) => {
                  const selecionado = draft.studyMode === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`flex w-full cursor-pointer flex-col gap-1 rounded-card border p-3 ${
                        selecionado
                          ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                          : 'border-edge bg-surface text-ink'
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <input
                          type="radio"
                          name="editor-modo-oclusao"
                          value={option.value}
                          checked={selecionado}
                          onChange={() =>
                            setDraft((atual) => ({ ...atual, studyMode: option.value }))
                          }
                          className="shrink-0"
                        />
                        {option.label}
                      </span>
                      <span className="text-sm text-ink-secondary">{option.effect}</span>
                    </label>
                  );
                })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {aba === 'historico' ? <EditorHistorico historico={historico} /> : null}
      </div>

      <footer
        className={`relative z-10 flex shrink-0 flex-col gap-3 border-t border-edge bg-surface px-4 pt-3 ${
          estreito ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : 'pb-3'
        }`}
      >
        {falha ? (
          <p role="alert" className="text-sm text-ink-secondary">
            {falha}
          </p>
        ) : null}
        {pediuMascara && semMarcacao ? (
          <p
            role="alert"
            className="rounded-input border border-edge bg-surface-muted px-3 py-2 text-sm text-ink"
          >
            {MASK_REQUIRED_MESSAGE}
          </p>
        ) : null}
        <div
          className={`flex gap-3 ${estreito ? 'flex-col' : 'flex-row flex-wrap items-center justify-end'}`}
        >
          <div className={`flex min-w-0 items-center gap-2 ${estreito ? 'w-full' : ''}`}>
            <button
              type="button"
              data-editor-acao="cancelar"
              onClick={cancelar}
              aria-keyshortcuts="Escape"
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-input border border-edge bg-surface px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover ${
                estreito ? 'min-w-0 flex-1' : ''
              }`}
            >
              Cancelar
              <kbd className={kbdClass}>Esc</kbd>
            </button>
          </div>
          <div className={`flex min-w-0 items-center gap-2 ${estreito ? 'w-full' : ''}`}>
            <button
              type="button"
              data-editor-acao="salvar"
              onClick={executarSalvar}
              disabled={salvando}
              aria-busy={salvando}
              aria-keyshortcuts="Meta+Enter Control+Enter"
              className={`${revisaoPrimaryActionClass} min-w-0 gap-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                estreito ? 'flex-1' : ''
              }`}
            >
              {salvando ? 'Salvando…' : 'Salvar'}
              {salvando ? null : <kbd className={kbdClass}>⌘↵</kbd>}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );

  if (estreito) {
    return createPortal(
      <>
        <div
          data-editor-scrim=""
          className="fixed inset-0 z-40 bg-ink/40"
          onClick={cancelar}
          aria-hidden="true"
        />
        <div
          ref={raizRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={tituloId}
          tabIndex={-1}
          onKeyDown={conterNaCamada}
          data-editor-na-revisao="folha"
          className="fixed inset-x-0 bottom-0 z-50 flex h-[min(544px,calc(100dvh-0.75rem))] w-full min-h-0 flex-col overflow-hidden rounded-t-modal border border-edge bg-surface text-ink outline-none"
        >
          {quadro}
        </div>
      </>,
      document.body,
    );
  }

  return (
    <div
      ref={raizRef}
      aria-labelledby={tituloId}
      tabIndex={-1}
      onKeyDown={conterNaCamada}
      data-editor-na-revisao="painel"
      className="flex h-[min(720px,calc(100dvh-6rem))] w-full min-w-0 flex-col overflow-hidden rounded-card border border-edge bg-surface text-ink outline-none"
    >
      {quadro}
    </div>
  );
}
